// useCountdownTimer.ts
import { ref, computed, onUnmounted } from 'vue'


/**
 * Custom hook to manage a countdown timer.
 * @param {number} duration - The duration of the countdown in seconds.
 * @returns {Object} - An object containing the time left, start, and stop methods.
 *
 * Usage:
 * const { timeLeft, start, stop } = useCountdownTimer(30);
 * start(); // Starts the countdown
 * stop(); // Stops the countdown
 */
export function useCountdownTimer(duration = 30) {
    const timeLeft = ref(duration);
    const timerId = ref<ReturnType<typeof setInterval> | null>(null);

    const start = () => {
        timeLeft.value = duration;
        timerId.value = setInterval(() => {
            timeLeft.value > 0 ?
                timeLeft.value-- :
                stop();
        }, 1000)
    }

    const stop = () => {
        if (timerId.value) {
            clearInterval(timerId.value);
            timerId.value = null;
        }
    }

    const isCountdownRunning = computed(() => timerId.value !== null)

    onUnmounted(() => {
        stop();
    })

    return { timeLeft, start, stop, isCountdownRunning }
}
